import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-7.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import {
  getGalleryMediaAspectRatio,
  getPlaceholderImage,
} from "../utils/utils.js";
import ImageModule from "./Modules/ImageModule.js";
import MultilineText from "./MultilineText.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  isFirstOnPage: boolean;
  queries: ContainerQueries;
  scheme: ColorScheme;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  onClickImage: (index: number) => void;
}

const ImageGalleryLayout7: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  queries,
  scheme,
  imageModules,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  onClickImage,
}) => {
  const TagName = isFirstOnPage ? "h1" : "h2";
  const SubtitleTagName = title ? "div" : TagName;
  const site = useAppSelector(selectActiveSite);

  const images = imageModules.length
    ? imageModules
    : new Array(4)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  return (
    <div
      className={ClassNames(
        moduleBaseStyles.Module__Wrapper,
        moduleBaseStyles["Module__Wrapper--small-padding"],
      )}
    >
      <div className={styles.ImageGalleryLayout7__Items}>
        <div className={styles.ImageGalleryLayout7__Items__Left}>
          {title && (
            <TagName
              className={ClassNames(
                moduleBaseStyles.Module__Title,
                styles.ImageGalleryLayout7__Title,
                `TextAlign--${textAlign.title}`,
                css({ color: scheme.main.title }),
              )}
            >
              {title}
            </TagName>
          )}
          <div className={styles.ImageGalleryLayout7__Items__LeftInner}>
            {images.slice(0, 2).map((imageModule, index) => (
              <div
                key={imageModule.id}
                className={ClassNames(styles.ImageGalleryLayout7__Item, {
                  [styles.ImageGalleryLayout7__ItemFirst]: index === 0,
                  [styles.ImageGalleryLayout7__ItemLast]: index === 3,
                })}
              >
                <ImageItem
                  imageModule={imageModule}
                  index={index}
                  slides={slides}
                  isPreview={isPreview}
                  pageId={pageId}
                  scheme={scheme}
                  onClickImage={onClickImage}
                  queries={queries}
                />
                {index === 1 && subtitle && (
                  <SubtitleTagName
                    className={ClassNames(
                      moduleBaseStyles.Module__Subtitle,
                      styles.ImageGalleryLayout7__Subtitle,
                      `TextAlign--${textAlign.subtitle}`,
                      css({ color: scheme.main.text }),
                    )}
                  >
                    <MultilineText text={subtitle} />
                  </SubtitleTagName>
                )}
              </div>
            ))}
          </div>
        </div>
        <div
          className={ClassNames(styles.ImageGalleryLayout7__Items__Right, {
            [styles["ImageGalleryLayout7__Items__Right--no-images"]]:
              images.length <= 2,
          })}
        >
          {images.slice(2, 4).map((imageModule, index) => (
            <div
              key={imageModule.id}
              className={ClassNames(styles.ImageGalleryLayout7__Item, {
                [styles.ImageGalleryLayout7__ItemFirst]: index === 0,
                [styles.ImageGalleryLayout7__ItemLast]: index === 3,
              })}
            >
              <ImageItem
                imageModule={imageModule}
                index={index + 2}
                slides={slides}
                isPreview={isPreview}
                pageId={pageId}
                scheme={scheme}
                onClickImage={onClickImage}
                key={imageModule.id}
                queries={queries}
              />
            </div>
          ))}
          <SubtitleTagName
            className={ClassNames(
              moduleBaseStyles.Module__Subtitle,
              styles.ImageGalleryLayout7__SubtitleMobile,
              `TextAlign--${textAlign.subtitle}`,
              css({ color: scheme.main.text }),
            )}
          >
            <MultilineText text={subtitle} />
          </SubtitleTagName>
        </div>
      </div>
    </div>
  );
};

interface ImageItemProps {
  imageModule: TranslatedModule<ImageModuleSettings>;
  index: number;
  slides: SlideImage[];
  isPreview: boolean;
  pageId: string;
  scheme: ColorScheme;
  onClickImage: (index: number) => void;
  queries: ContainerQueries;
}

const ImageItem = ({
  imageModule,
  index,
  slides,
  isPreview,
  pageId,
  scheme,
  onClickImage,
  queries,
}: ImageItemProps) => {
  const item = slides[index];
  return (
    <a
      href={isPreview || !item ? undefined : item.src}
      key={imageModule.id}
      onClick={(e) => {
        e.preventDefault();
        onClickImage(index);
      }}
    >
      <ImageModule
        aspectRatio={getAspectRatio(index, queries)}
        sizes="50vw"
        lazyLoad={true}
        width={1920}
        translatedModule={imageModule}
        isPreview={isPreview}
        isSlider={false}
        pageId={pageId}
        scheme={scheme}
        showOverlay={false}
      />
    </a>
  );
};

const getAspectRatio = (index: number, queries: ContainerQueries): number => {
  const aspectRatios = [0.75, 1, 1, 0.6667] as const;

  if (queries["Query--medium"])
    return getGalleryMediaAspectRatio(index, "layout-7");

  return aspectRatios[index] ?? getGalleryMediaAspectRatio(index, "layout-7");
};

export default ImageGalleryLayout7;
